import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import { TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { loginPageStyles } from './LoginPage.styles'

function StatusBlockPage() {
    const { t } = useTranslation()
    const { user, isAuthenticated, clearStatus, logout } = useAuth()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    if (!isAuthenticated) return <Navigate to="/login" replace />
    if (!user?.status) return <Navigate to="/app" replace />

    const title = t(`auth.statusBlock.${user.status}.title`, { defaultValue: t('auth.statusBlock.fallback.title') })
    const description = t(`auth.statusBlock.${user.status}.description`, { status: user.status, defaultValue: t('auth.statusBlock.fallback.description', { status: user.status }) })

    const handleClear = async () => {
        setError('')
        setLoading(true)
        try {
            await clearStatus()
        } catch (err) {
            console.error('Clear status failed:', err)
            setError(t('auth.statusBlock.clearFailed'))
        } finally {
            setLoading(false)
        }
    }

    return (
        <Container maxWidth="xs" sx={loginPageStyles.container}>
            <Box sx={loginPageStyles.logoWrapper}>
                <Box sx={{ ...loginPageStyles.logoIcon, bgcolor: 'error.main' }}>
                    <TriangleAlert size={28} color="white" />
                </Box>
                <Typography variant="h5" fontWeight={700} textAlign="center">
                    {title}
                </Typography>
            </Box>

            <Paper elevation={0} sx={loginPageStyles.paper}>
                {error && (
                    <Alert severity="error" sx={loginPageStyles.errorAlert}>{error}</Alert>
                )}

                <Typography variant="body2" color="text.secondary" mb={3}>
                    {description}
                </Typography>

                <Box sx={loginPageStyles.form}>
                    <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleClear}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {loading ? t('auth.statusBlock.processing') : t('auth.statusBlock.clearButton')}
                    </Button>

                    <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        onClick={logout}
                    >
                        {t('auth.signOut')}
                    </Button>
                </Box>
            </Paper>
        </Container>
    )
}

export default StatusBlockPage
